import { toWSDateString } from "@wingspanhq/fe-component-library";
import {
  ActorReference,
  ActorReferenceOneOf,
  Payable
} from "@wingspanhq/wingspan-api-sdk";
import React, { useMemo } from "react";
import { queriesPersons } from "@query";
import { useSession } from "@hooks/useSession";
import { selectorPersonDisplayName } from "../../../shared/selectors/selectorPersonDisplayName";
import { isSameDateSafe } from "../../../utils/dates";
import { Activity, ActivityItem } from "../Activity";

type Props = { payable: Payable };

/**
 * A payable's `actors` entries are opaque references: either the `SYSTEM` /
 * `UNKNOWN` sentinels or an id. Only an id is resolvable to a person, and a
 * reference the caller can't resolve renders without a "by …" suffix rather than
 * being attributed to the wrong teammate.
 */
const actorPersonId = (actor?: ActorReference): string | undefined =>
  typeof actor === "string" &&
  actor !== ActorReferenceOneOf.SYSTEM &&
  actor !== ActorReferenceOneOf.UNKNOWN
    ? actor
    : undefined;

const byLine = (label: string, actor?: string | null) =>
  actor ? `${label} by ${actor}` : label;

export const PayableActivity: React.FC<Props> = props => {
  const { payable } = props;
  const events = payable.events;
  const actors = payable.actors;
  const { personId } = useSession();

  // The payable's notifications go to the payer — the account reading this
  // detail. v3 never inlines the `payer` perspective for a payer caller, so the
  // payer-side email is the signed-in person's.
  const queryPerson = queriesPersons.getPerson.useQuery(
    { personId },
    { enabled: !!personId }
  );
  const payerEmail = queryPerson.data?.email;
  const payeeEmail = payable.payee?.email;

  // An actor id is only resolvable through the account roster: `getPerson` is
  // self-only, so reading a teammate one id at a time 404s. `listPersons` is
  // scoped to the session's bound account and inlines each person's compliance
  // entity, which is where a name lives when there is one.
  const queryAccountPersons = queriesPersons.listPersons.useQueryAll();

  const actorLabel = (actor?: ActorReference) => {
    const actorId = actorPersonId(actor);
    const person = queryAccountPersons.flatData?.find(
      person => person.id === actorId
    );

    return person ? selectorPersonDisplayName(person) : undefined;
  };

  const approverLabel = actorLabel(actors?.approvedBy);
  const preApproverLabel = actorLabel(actors?.preApprovedBy);
  const unapproverLabel = actorLabel(actors?.unapprovedBy);

  const activity = useMemo(() => {
    const activity: ActivityItem[] = [];

    if (events?.createdAt) {
      activity.push({
        title: "Payable was created",
        description: toWSDateString(events.createdAt),
        date: events.createdAt
      });
    }

    if (events?.openedAt) {
      activity.push({
        title: payerEmail ? `Sent to ${payerEmail}` : "Invoice opened",
        description: toWSDateString(events.openedAt),
        date: events.openedAt
      });
    }

    const emailReceivedAt = events?.emailReceivedAt?.[0];
    if (emailReceivedAt) {
      activity.push({
        title: byLine("Received", payerEmail),
        description: toWSDateString(emailReceivedAt),
        date: emailReceivedAt
      });
    }

    // A view recorded after the money moved is a stale projection, not a real
    // read of the open invoice, so those are dropped. Of the rest only the
    // first view and the first view more than an hour later are shown, to keep
    // a mail client's repeat opens from flooding the timeline.
    const viewedAt = (events?.emailViewedAt ?? [])
      .filter(date => (events?.paidAt ? events.paidAt > date : true))
      .sort((left, right) => left.getTime() - right.getTime());
    const firstViewedAt = viewedAt[0];
    const laterViewedAt = firstViewedAt
      ? viewedAt.find(
          date => date.getTime() - firstViewedAt.getTime() > 60 * 60 * 1000
        )
      : undefined;

    [firstViewedAt, laterViewedAt].forEach(date => {
      if (date) {
        activity.push({
          title: byLine("Viewed", payerEmail),
          description: toWSDateString(date),
          date
        });
      }
    });

    if (events?.preApprovedAt) {
      activity.push({
        title: byLine("Pre-approved", preApproverLabel),
        description: toWSDateString(events.preApprovedAt),
        date: events.preApprovedAt
      });
    }

    if (events?.approvedAt) {
      activity.push({
        title: byLine("Approved", approverLabel),
        description: toWSDateString(events.approvedAt),
        date: events.approvedAt
      });
    }

    if (events?.unapprovedAt) {
      activity.push({
        title: byLine("Unapproved", unapproverLabel),
        description: toWSDateString(events.unapprovedAt),
        date: events.unapprovedAt
      });
    }

    // The due-today reminder fires on the send date for an invoice that is
    // already due, which would duplicate the "Sent to" line.
    const reminderDates = [
      events?.sentDueIn3DaysAt,
      isSameDateSafe(events?.openedAt, events?.sentDueTodayAt ?? undefined)
        ? undefined
        : events?.sentDueTodayAt,
      events?.sentDue3DaysAgoAt,
      events?.sentDue7DaysAgoAt
    ];

    reminderDates.forEach(date => {
      if (date) {
        activity.push({
          title: payerEmail
            ? `Reminder sent to ${payerEmail}`
            : "Reminder sent",
          description: toWSDateString(date),
          date
        });
      }
    });

    if (events?.paymentInTransitAt) {
      activity.push({
        title: `Payment in transit on ${toWSDateString(
          events.paymentInTransitAt
        )}`,
        description: "Waiting on wire transfer to arrive",
        date: events.paymentInTransitAt
      });
    }

    (events?.paymentFailedAt || []).forEach(failedAt => {
      activity.push({
        title: "Payment failed",
        description: toWSDateString(failedAt),
        date: failedAt
      });
    });

    if (events?.partiallyPaidAt) {
      activity.push({
        title: "Partially paid",
        description: toWSDateString(events.partiallyPaidAt),
        date: events.partiallyPaidAt
      });
    }

    if (events?.paidAt) {
      activity.push({
        title: `Paid on ${toWSDateString(events.paidAt)}`,
        description: "Payable was paid",
        date: events.paidAt
      });
    }

    if (events?.markedPaidAt) {
      activity.push({
        title: "Marked as paid",
        description: toWSDateString(events.markedPaidAt),
        date: events.markedPaidAt
      });
    }

    if (events?.paidOffPlatformAt) {
      activity.push({
        title: "Marked as paid off-platform",
        description: toWSDateString(events.paidOffPlatformAt),
        date: events.paidOffPlatformAt
      });
    }

    if (events?.depositConfirmedAt) {
      activity.push({
        title: "Deposit confirmed",
        description: toWSDateString(events.depositConfirmedAt),
        date: events.depositConfirmedAt
      });
    }

    if (events?.memberAcceptedAt || events?.acceptedAt) {
      const acceptedAt = (events.acceptedAt ?? events.memberAcceptedAt)!;
      activity.push({
        title: "Invoice accepted by contractor",
        description: toWSDateString(acceptedAt),
        date: acceptedAt
      });
    }

    if (events?.memberDisputedAt) {
      activity.push({
        title: payeeEmail ? `Disputed by ${payeeEmail}` : "Invoice disputed",
        description: toWSDateString(events.memberDisputedAt),
        date: events.memberDisputedAt
      });
    }

    if (events?.clientResolvedDisputeAt) {
      activity.push({
        title: "Dispute resolved",
        description: `Dispute resolved on ${toWSDateString(
          events.clientResolvedDisputeAt
        )}`,
        date: events.clientResolvedDisputeAt
      });
    }

    if (events?.memberResubmittedAt) {
      activity.push({
        title: payeeEmail
          ? `Resubmitted by ${payeeEmail}`
          : "Resubmitted by contractor",
        description: `Contractor resolved the dispute on ${toWSDateString(
          events.memberResubmittedAt
        )}`,
        date: events.memberResubmittedAt
      });
    }

    if (events?.clientDeclinedAt) {
      activity.push({
        title: byLine("Rejected", payerEmail),
        description: toWSDateString(events.clientDeclinedAt),
        date: events.clientDeclinedAt
      });
    }

    if (events?.cancelledAt) {
      activity.push({
        title: "Invoice cancelled",
        description: toWSDateString(events.cancelledAt),
        date: events.cancelledAt
      });
    }

    if (events?.refundedAt) {
      activity.push({
        title: "Invoice refunded",
        description: toWSDateString(events.refundedAt),
        date: events.refundedAt
      });
    }

    return activity.sort((a, b) => {
      if (a.date > b.date) return -1;
      if (a.date < b.date) return 1;
      return 0;
    });
  }, [
    events,
    payerEmail,
    payeeEmail,
    approverLabel,
    preApproverLabel,
    unapproverLabel
  ]);

  return <Activity activity={activity} />;
};
